import React, { useState } from 'react';
import {
  Box,
  Tabs,
  Tab,
  TextField,
  Paper,
  Toolbar,
  Tooltip,
  IconButton,
  Divider
} from '@mui/material';
import {
  FormatBold as BoldIcon,
  FormatItalic as ItalicIcon,
  FormatListBulleted as ListIcon,
  FormatListNumbered as NumberedListIcon,
  Code as CodeIcon,
  Link as LinkIcon,
  Image as ImageIcon
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { createMarkdownComponents } from '../common/MarkdownComponents';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const MarkdownEditor = ({ value, onChange }) => {
  const [tab, setTab] = useState(0);
  const [selection, setSelection] = useState({ start: 0, end: 0 });
  const isDarkMode = document.documentElement.classList.contains('dark');

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  const handleTextChange = (e) => {
    onChange(e.target.value);
    
    // Save current selection for toolbar buttons
    setSelection({
      start: e.target.selectionStart,
      end: e.target.selectionEnd
    });
  };

  const insertMarkdown = (markdownFn) => {
    const textBefore = value.substring(0, selection.start);
    const selectedText = value.substring(selection.start, selection.end);
    const textAfter = value.substring(selection.end);
    
    const newText = textBefore + markdownFn(selectedText) + textAfter;
    onChange(newText);
  };

  // Toolbar button handlers
  const handleBold = () => {
    insertMarkdown(text => `**${text || 'bold text'}**`);
  };

  const handleItalic = () => {
    insertMarkdown(text => `*${text || 'italic text'}*`);
  };

  const handleBulletList = () => {
    insertMarkdown(text => text ? text.split('\\n').map(line => `- ${line}`).join('\\n') : '- List item');
  };

  const handleNumberedList = () => {
    insertMarkdown(text => text ? text.split('\\n').map((line, i) => `${i+1}. ${line}`).join('\\n') : '1. List item');
  };

  const handleCode = () => {
    insertMarkdown(text => `\`${text || 'code'}\``);
  };

  const handleCodeBlock = () => {
    insertMarkdown(text => `\`\`\`\n${text || 'code block'}\n\`\`\``);
  };

  const handleLink = () => {
    insertMarkdown(text => `[${text || 'link text'}](url)`);
  };

  const handleImage = () => {
    insertMarkdown(text => `![${text || 'alt text'}](image-url)`);
  };

  return (
    <Box>
      <Tabs value={tab} onChange={handleTabChange}>
        <Tab label="Write" />
        <Tab label="Preview" />
      </Tabs>
      
      <TabPanel value={tab} index={0}>
        <Paper variant="outlined">
          <Toolbar variant="dense">
            <Tooltip title="Bold">
              <IconButton size="small" onClick={handleBold}>
                <BoldIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Italic">
              <IconButton size="small" onClick={handleItalic}>
                <ItalicIcon />
              </IconButton>
            </Tooltip>
            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
            <Tooltip title="Bullet List">
              <IconButton size="small" onClick={handleBulletList}>
                <ListIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Numbered List">
              <IconButton size="small" onClick={handleNumberedList}>
                <NumberedListIcon />
              </IconButton>
            </Tooltip>
            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
            <Tooltip title="Inline Code">
              <IconButton size="small" onClick={handleCode}>
                <CodeIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Code Block">
              <IconButton size="small" onClick={handleCodeBlock}>
                <CodeIcon sx={{ mr: -0.5 }} />
                <CodeIcon sx={{ ml: -0.5 }} />
              </IconButton>
            </Tooltip>
            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
            <Tooltip title="Link">
              <IconButton size="small" onClick={handleLink}>
                <LinkIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Image">
              <IconButton size="small" onClick={handleImage}>
                <ImageIcon />
              </IconButton>
            </Tooltip>
          </Toolbar>
          <TextField
            value={value}
            onChange={handleTextChange}
            onSelect={(e) => {
              setSelection({
                start: e.target.selectionStart,
                end: e.target.selectionEnd
              });
            }}
            multiline
            fullWidth
            minRows={10}
            variant="outlined"
            InputProps={{
              sx: { 
                fontFamily: 'monospace',
                fontSize: '0.9rem'
              }
            }}
            sx={{ 
              '& .MuiOutlinedInput-root': {
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
                border: 'none',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
                borderTop: '1px solid rgba(0, 0, 0, 0.12)'
              }
            }}
          />
        </Paper>
      </TabPanel>
      
      <TabPanel value={tab} index={1}>
        <Paper variant="outlined" sx={{ p: 2, minHeight: '300px' }}>
          <Box className="prose prose-sm sm:prose lg:prose-lg dark:prose-invert max-w-none">
            <ReactMarkdown
              components={createMarkdownComponents(isDarkMode)}
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
            >
              {value}
            </ReactMarkdown>
          </Box>
        </Paper>
      </TabPanel>
    </Box>
  );
};

export default MarkdownEditor;